/**
 * React Security Components and Utilities
 * Frontend protection against common attacks
 */

// =============================================================================
// 1. API Error Handler with Auto-Retry and IP Block Detection
// =============================================================================

import { useState, useCallback } from 'react';

export const useSecureAPI = () => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');

  const secureRequest = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      // Check for IP blocking
      if (response.status === 403) {
        const data = await response.json();
        if (data.error === 'Access denied' || data.error === 'Security violation') {
          setIsBlocked(true);
          setBlockMessage(data.message || 'Your access has been blocked.');
          throw new Error('IP_BLOCKED');
        }
      }

      // Check for rate limiting
      if (response.status === 429) {
        const data = await response.json();
        throw new Error(data.message || 'Too many requests. Please slow down.');
      }

      return response;
    } catch (error) {
      if (error.message === 'IP_BLOCKED') {
        // Show blocking message to user
        console.error('IP Blocked:', blockMessage);
      }
      throw error;
    }
  }, [blockMessage]);

  return { secureRequest, isBlocked, blockMessage };
};

// =============================================================================
// 2. XSS Protection - Sanitize User Input
// =============================================================================

export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// =============================================================================
// 3. CSRF Token Handler
// =============================================================================

export const getCSRFToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  
  return cookieValue;
};

// =============================================================================
// 4. Rate Limit Handler Component
// =============================================================================

export const useRateLimiter = (maxRequests = 10, windowMs = 60000) => {
  const [requests, setRequests] = useState([]);

  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const recentRequests = requests.filter(time => now - time < windowMs);
    
    if (recentRequests.length >= maxRequests) {
      return false; // Rate limit exceeded
    }
    
    setRequests([...recentRequests, now]);
    return true; // OK to proceed
  }, [requests, maxRequests, windowMs]);

  return checkRateLimit;
};

// =============================================================================
// 5. IP Block Warning Component
// =============================================================================

export const IPBlockedWarning = ({ isBlocked, message, onContact }) => {
  if (!isBlocked) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: '40px',
        borderRadius: '8px',
        maxWidth: '500px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#d32f2f', marginBottom: '20px' }}>Access Blocked</h2>
        <p style={{ marginBottom: '30px', color: '#666' }}>
          {message || 'Your IP address has been temporarily blocked due to suspicious activity.'}
        </p>
        <p style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>
          If you believe this is a mistake, please contact the administrator.
        </p>
        {onContact && (
          <button
            onClick={onContact}
            style={{
              backgroundColor: '#1976d2',
              color: '#fff',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
            }}
          >
            Contact Support
          </button>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// 6. Secure Form Component
// =============================================================================

export const SecureForm = ({ children, onSubmit, ...props }) => {
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const data = {};
    
    // Sanitize all form inputs
    for (let [key, value] of formData.entries()) {
      data[key] = sanitizeInput(value);
    }
    
    // Add CSRF token
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      data.csrfmiddlewaretoken = csrfToken;
    }
    
    if (onSubmit) {
      onSubmit(data, e);
    }
  };

  return (
    <form onSubmit={handleSubmit} {...props}>
      <input type="hidden" name="csrfmiddlewaretoken" value={getCSRFToken()} />
      {children}
    </form>
  );
};

// =============================================================================
// 7. Request Caching to Reduce Server Load
// =============================================================================

class RequestCache {
  constructor(ttl = 60000) {
    this.cache = new Map();
    this.ttl = ttl; // Time to live in milliseconds
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  clear() {
    this.cache.clear();
  }
}

export const apiCache = new RequestCache(60000); // 1 minute cache

// =============================================================================
// 8. Secure API Wrapper with Caching
// =============================================================================

export const secureAPICall = async (url, options = {}, useCache = true) => {
  // Check cache first for GET requests
  if (useCache && (!options.method || options.method === 'GET')) {
    const cached = apiCache.get(url);
    if (cached) {
      return cached;
    }
  }

  const csrfToken = getCSRFToken();
  
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
      ...options.headers,
    },
  });

  if (response.status === 403) {
    const data = await response.json();
    if (data.error === 'Access denied') {
      throw new Error('IP_BLOCKED');
    }
  }

  if (response.status === 429) {
    throw new Error('RATE_LIMITED');
  }

  const data = await response.json();

  // Cache successful GET requests
  if (useCache && (!options.method || options.method === 'GET')) {
    apiCache.set(url, data);
  }

  return data;
};

// =============================================================================
// 9. Complete Example Usage
// =============================================================================

/*
import React, { useState, useEffect } from 'react';
import { 
  useSecureAPI, 
  IPBlockedWarning, 
  SecureForm,
  secureAPICall 
} from './security';

function App() {
  const { secureRequest, isBlocked, blockMessage } = useSecureAPI();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    // Secure API call with caching
    secureAPICall('/api/articles/?lang=en')
      .then(data => setArticles(data.results || []))
      .catch(error => {
        if (error.message === 'IP_BLOCKED') {
          console.log('Blocked!');
        }
      });
  }, []);

  const handleLogin = async (formData) => {
    try {
      const response = await secureRequest('/api/auth/login/', {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      // Handle login...
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <>
      <IPBlockedWarning 
        isBlocked={isBlocked} 
        message={blockMessage}
        onContact={() => window.location.href = '/contact'}
      />
      
      <SecureForm onSubmit={handleLogin}>
        <input type="text" name="username" required />
        <input type="password" name="password" required />
        <button type="submit">Login</button>
      </SecureForm>
    </>
  );
}
*/